import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus } from "lucide-react";
import { toast } from "sonner";

export default function Support() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", body: "", priority: "MEDIUM", category: "question" });
  const load = async () => {
    // Reuse existing support ticket endpoint; backend already filters by submitter via tenant scope
    try { const { data } = await api.get("/admin/support/tickets"); setItems(data); } catch { setItems([]); }
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.post("/support/tickets", form); toast.success("Ticket raised"); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="dealer-support">
      <div className="flex items-start justify-between">
        <div><h1 className="font-display text-2xl sm:text-3xl font-black">Support center</h1><p className="text-muted-foreground">Product complaints, delivery issues, payment problems and warranty claims.</p></div>
        <Button onClick={() => setOpen(true)} data-testid="ticket-create-btn"><Plus className="w-4 h-4 mr-1" /> Raise ticket</Button>
      </div>
      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Subject</TableHead><TableHead>Category</TableHead><TableHead>Priority</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((t) => (
              <TableRow key={t.id} data-testid={`ticket-${t.id}`}>
                <TableCell className="font-semibold">{t.subject}<div className="text-xs text-muted-foreground line-clamp-1">{t.body}</div></TableCell>
                <TableCell><Badge variant="outline">{t.category || "—"}</Badge></TableCell>
                <TableCell><Badge variant="outline">{t.priority}</Badge></TableCell>
                <TableCell><Badge>{t.status}</Badge></TableCell>
                <TableCell className="font-mono text-[10px]">{new Date(t.created_at).toLocaleDateString()}</TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">No tickets yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Raise support ticket</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="ticket-subject" /></div>
            <div><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="ticket-category"><SelectValue /></SelectTrigger>
                <SelectContent>{["question","product","delivery","payment","warranty"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Priority</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{["LOW","MEDIUM","HIGH","URGENT"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Description</Label><textarea className="w-full border rounded p-2 text-sm" rows="5" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} data-testid="ticket-body" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} disabled={!form.subject || !form.body} data-testid="ticket-save">Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
